import React, {memo, useEffect, useRef, useState} from 'react'
import {withStyles} from '@material-ui/core';
import PropTypes from 'prop-types';
import Form from 'components/Form';
import {getInitValues as getExInitValues} from './constants/exclusion.values';
import {getInitValues as getSeInitValues} from './constants/selection.values';
import {connect} from 'react-redux'
import EspdService from 'services/EspdService';
import {withRouter} from "react-router";
import Loading from 'components/Loading';
import getObjectValue from 'libs/getObjectValue'
import {DemandValidation, DemandValidationKeys} from 'constants/DemandValidation';
import JEDZFormContent from "./JEDZFormContent";
import VS from "constants/ValidationShema";
import Tr from "translations/Translations";
import {isDemandModification} from "libs/getDemandStatus";
import ModificationService from "services/ModificationService";
import SaveFormikValues from "components/SaveFormikValues";
import reduxStore from "redux/store";
import useStepperStateCloseValidator from "hooks/useStepperStateCloseValidator";
import {setFormDraftLoaded} from "redux/actions";
import Confirm from "components/popup/confirm/Confirm";
import AutoSaveFormValues from "components/AutoSaveFormValues";
import saveFormDraft from "libs/saveFormDraft";


const style = theme => ({
    root: {
        width: '100%'
    },
    icon: {
        marginRight: theme.spacing(1),
    }
})

function JEDZ(
    {
        classes,
        stepperState,
        match,
        demandValidation,
        espdList,
        setFormDraftLoaded,
        demandHeader
    }) {
    const id = match.params.id;
    const section = demandValidation.find(x => x.section === DemandValidation.ESPD);
    const sectionCompleted = section ? section.status === 'OK' : false;
    const FORM_ID = DemandValidationKeys.ESPD;
    const typeChanged = useRef(false);
    const [draftConfirm, setDraftConfirm] = useState(false);
    const [working, setWorking] = useState(false);
    const autoSaveFormDraftActive = demandHeader?.applicationClientSettings.autoSaveFormDraft.active;
    useStepperStateCloseValidator(FORM_ID, ()=> {
            if(typeChanged.current)
                return false;
            const dirty = reduxStore.getState()?.formValues?.[FORM_ID + "_dirty"];
            const draftLoaded = reduxStore.getState()?.formValues?.[FORM_ID + "_draftLoaded"];
            return dirty !== true && !draftLoaded;
        },
        autoSaveFormDraftActive ? () => EspdService.deleteDraft(id) : undefined,
        autoSaveFormDraftActive ? () => saveFormDraft(id, EspdService.saveDraft, FORM_ID) : undefined);

    useEffect(() => {
        EspdService.getList(id).then(() => {
            if (autoSaveFormDraftActive)
                EspdService.checkForDraft(id).then(res => setDraftConfirm(res.result))
        });

        return () => EspdService.clearEspdList();
    }, [id, autoSaveFormDraftActive])


    const handleSubmit = (values) => {
        EspdService.save(id, values)
    }

    const handleJedzTypeChange = (separateForEachDealing) => {
        typeChanged.current = true;
        EspdService.clearEspdList();
        EspdService.getList(id, separateForEachDealing);
    }

    const loadEspdDraft = () => {
        setDraftConfirm(false);
        setWorking(true);
        EspdService.loadDraft(id).then(res => setFormDraftLoaded(FORM_ID, res.success));
        setWorking(false);
    }


    const getField = (id, defaultValue = '') => getObjectValue(id, espdList, defaultValue)
    const getSingleJedzValue = (idPrefix) => {
        return {
            identity: getField(`${idPrefix}.identity`),
            espdBuyerType: getField(`${idPrefix}.espdBuyerType`),
            procedureType: getField(`${idPrefix}.procedureType`),
            noticeReferenceNumber: getField(`${idPrefix}.noticeReferenceNumber`, null),
            e1: getExInitValues('e1', espdList, idPrefix),
            e2: getExInitValues('e2', espdList, idPrefix),
            e3: getExInitValues('e3', espdList, idPrefix),
            e4: getExInitValues('e4', espdList, idPrefix),
            s1: getSeInitValues('s1', espdList, idPrefix),
            s2: getSeInitValues('s2', espdList, idPrefix),
            s3: getSeInitValues('s3', espdList, idPrefix),
            s4: getSeInitValues('s4', espdList, idPrefix),
            n1: {1: true},
            qualifications: getField(`${idPrefix}.selection`),
            name: getField(`${idPrefix}.name`),
            description: getField(`${idPrefix}.description`),
            number: getField(`${idPrefix}.number`),
            orgName: getField(`${idPrefix}.orgName`),
            orgCountry: getField(`${idPrefix}.orgCountry`),
        }
    }


    const getInitialValues = () => {

        const separateForEachDealing = getField("separateForEachDealing", "false") === 'true';

        const initVal = {
            _editable: section ? section.editable : true,
            separateForEachDealing: separateForEachDealing.toString(),
        };
        if (separateForEachDealing) {
            const espds = espdList.espds.map((el, index) => {
                return getSingleJedzValue(`espds.${index}`);
            });
            initVal.espds = espds;
        } else {
            const espd = getSingleJedzValue('espd');
            initVal.espd = espd;
        }
        return initVal;
    };

    const searchForBooleanValue = (espd, sections, searchedBooleanValue) => {
        let found = false;
        for (let s of sections) {
            found = findBooleanValue(espd, s, searchedBooleanValue) ? true : false;
            if (found)
                break;
        }
        return found;
    }

    const findBooleanValue = (espd, section, searchedBooleanValue) => {
        return Object.values(espd[section]).some(x => {
            return searchedBooleanValue ? x : !x;
        });
    }

    const validationSchema = () => VS.lazy((values) => {
        const separateForEachDealing = getObjectValue('separateForEachDealing', values, 'true');

        const getEspdVSObject = (espd) => {
            const qualifications = getObjectValue('qualifications', espd, 'no');
            const buyerType = getObjectValue('espdBuyerType', espd, 'CLASSIC');

            const qualiOk = qualifications === 'no' || searchForBooleanValue(espd, ['s1', 's2', 's3', 's4'], true);
            const buyerTypeOk = buyerType === 'SECTOR' || !searchForBooleanValue(espd, ['e1', 'e2'], false);
            return VS.object({
                qualifications: VS.string().test('ESPDQualifications', Tr("jedz_qualifications_missing"), (qualifications) => {
                    return qualiOk;
                }),
                espdBuyerType: VS.string().test('ESPDBuyerType', Tr("espd_buyer_type_CLASSIC_all_e1_e2_must_be_true"), () => {
                    return buyerTypeOk;
                })
            });
        };

        if (separateForEachDealing === 'true') {
            if (values.espds) {
                return VS.object({
                    espds: VS.arrayOf(VS.lazy(espd => getEspdVSObject(espd)))
                })
            }
        } else {
            if (values.espd) {
                return VS.object(
                    {
                        espd: VS.lazy(espd => getEspdVSObject(espd))
                    })
            }
        }
        return VS.any();
    });
    if (!espdList || working) return <Loading/>;

    return (
        <div className={classes.root}>

            <Form
                initialValues={getInitialValues()}
                onSubmit={handleSubmit}
                validationSchema={validationSchema()}
                id={FORM_ID}
                afterSubmitFunc={() => {
                    if (isDemandModification()) {
                        ModificationService.isDemandChanged(id);
                    }
                }}
            >
                {(formikProps) => (
                    <>
                        <SaveFormikValues
                            formikProps={formikProps}
                            id={FORM_ID}
                            save={FORM_ID === stepperState}
                        />
                        {autoSaveFormDraftActive &&
                        <AutoSaveFormValues
                            formikProps={formikProps}
                            id={id}
                            formId={FORM_ID}
                            saveFunc={(formValues) => {
                                EspdService.saveDraft(id, formValues)
                            }}
                        />
                        }
                        <JEDZFormContent
                            formikProps={formikProps}
                            handleJedzTypeChange={handleJedzTypeChange}
                            demandId={id}
                            sectionCompleted={sectionCompleted}
                        />
                    </>
                )}
            </Form>
            <Confirm
                open={draftConfirm}
                onConfirm={loadEspdDraft}
                onCancel={() => setDraftConfirm(false)}
                confirmText="form_draft_load_confirm_text"
                confirmTitle="form_draft_load_confirm_title"
            />
        </div>
    )
}

JEDZ.propTypes = {
    classes: PropTypes.any,
}

export default connect((state) => ({
    stepperState: state.stepper.state,
    espdList: state.resources.espdList,
    demandValidation: state.resources.demandValidation,
    demandHeader: state.resources.demandHeader
}), {setFormDraftLoaded})(withRouter(withStyles(style)(memo(JEDZ))));